import { PROFILER_SEARCH_FIELDS } from '../../utils/constants/fields';
import { mapFieldsToState } from '../../utils/utils';
import {
  readSavedProfilerColumns,
  withNewProfilerColumns
} from '../../utils/constants/profilerColumns';
import {
  _columnFiltersFromJson,
  _columnFiltersToJson,
  getActiveSpotlightsBySearchResultsPage,
  getColumnFiltersBySearchResultsPage,
  getProfilerColumnsBySearchResultsPage,
  getProfilerExtendedFinancialsToggleBySearchResultsPage,
  getProfilerHeatmapCollapsedBySearchResultsPage,
  getProfilerHeatmapColorCodingSelectionBySearchResultsPage,
  getProfilerHeatmapGroupingToggleBySearchResultsPage,
  getProfilerHeatmapTileSizeSelectionBySearchResultsPage,
  getProfilerPreFinancialsOnlySelectionsBySearchResultsPage,
  getProfilerSortSelectionBySearchResultsPage,
  getProfilerViewSelectionBySearchResultsPage,
  hasSavedProfilerHeatmapGroupingToggleBySearchResultsPage,
  coerceProfilerColumnsForLite
} from '../search/SearchResults/constants/constants';

export const getProfilerColumnsByProjectView = (searchId = null) => {
  const profilerColumns = readSavedProfilerColumns(`pcbpv-${searchId}`);
  if (profilerColumns) {
    return withNewProfilerColumns(profilerColumns, getInitialProfilerColumnsProjectView());
  }

  const fromSearchResultsPage = getProfilerColumnsBySearchResultsPage(searchId);
  if (fromSearchResultsPage) {
    // if the user has column customizations in the search result page here the feedback column is being added
    const feedbackColumn = getInitialProfilerColumnsProjectView().find(
      (column) => column.id === PROFILER_PROJECT_VIEW_COLUMNS.FEEDBACK
    );
    fromSearchResultsPage.splice(fromSearchResultsPage.length - 1, 0, feedbackColumn);
    return withNewProfilerColumns(fromSearchResultsPage, getInitialProfilerColumnsProjectView());
  }

  return null;
};

export const saveProfilerColumnsByProjectView = (profilerColumns, searchId) => {
  if (!profilerColumns?.length || !searchId) {
    return;
  }

  localStorage.setItem(`pcbpv-${searchId}`, JSON.stringify(profilerColumns));
};

export const getProfilerExtendedFinancialsToggleByProjectView = (searchId) => {
  const isToggled = localStorage.getItem(`peftbpv-${searchId}`);
  if (isToggled) {
    return JSON.parse(isToggled);
  }

  return getProfilerExtendedFinancialsToggleBySearchResultsPage(searchId);
};

export const saveProfilerExtendedFinancialsToggleByProjectView = (isToggled, searchId) => {
  if (typeof isToggled !== 'boolean' || !searchId) {
    return;
  }

  localStorage.setItem(`peftbpv-${searchId}`, JSON.stringify(isToggled));
};

export const getProfilerSortSelectionByProjectView = (searchId) => {
  return (
    localStorage.getItem(`pssbpv-${searchId}`) ||
    getProfilerSortSelectionBySearchResultsPage(searchId)
  );
};

export const saveProfilerSortSelectionByProjectView = (selectedOption, searchId) => {
  if (typeof selectedOption !== 'string' || !searchId) {
    return;
  }

  localStorage.setItem(`pssbpv-${searchId}`, selectedOption);
};

// TD-2733 — the stacked heatmap can be folded away for a full-height list, per project
// and per team-feedback request. Falls back to the search page's choice so a project
// opened for the first time inherits how the analyst left the search, which is what every
// other heatmap preference in this file already does.
export const getProfilerHeatmapCollapsedByProjectView = (searchId) => {
  const isCollapsed = localStorage.getItem(`phcolsbpv-${searchId}`);
  if (isCollapsed) {
    return JSON.parse(isCollapsed);
  }

  return getProfilerHeatmapCollapsedBySearchResultsPage(searchId);
};

export const saveProfilerHeatmapCollapsedByProjectView = (isCollapsed, searchId) => {
  if (typeof isCollapsed !== 'boolean' || !searchId) {
    return;
  }

  localStorage.setItem(`phcolsbpv-${searchId}`, JSON.stringify(isCollapsed));
};

export const getProfilerViewSelectionByProjectView = (searchId) => {
  return (
    localStorage.getItem(`pvsbsp-${searchId}`) ||
    getProfilerViewSelectionBySearchResultsPage(searchId)
  );
};

export const saveProfilerViewSelectionByProjectView = (selectedView, searchId) => {
  if (typeof selectedView !== 'string' || !searchId) {
    return;
  }

  localStorage.setItem(`pvsbsp-${searchId}`, selectedView);
};

export const getProfilerPreFinancialsOnlySelectionsByProjectView = (searchId) => {
  const previousSelections = localStorage.getItem(`ppfosbpv-${searchId}`);
  if (previousSelections) {
    return JSON.parse(previousSelections);
  }

  return getProfilerPreFinancialsOnlySelectionsBySearchResultsPage(searchId);
};

export const saveProfilerPreFinancialsOnlySelectionsByProjectView = (selections, searchId) => {
  if (!selections?.length || !searchId) {
    return;
  }

  localStorage.setItem(`ppfosbpv-${searchId}`, JSON.stringify(selections));
};

export const getProfilerHeatmapTileSizeSelectionByProjectView = (searchId) => {
  return (
    localStorage.getItem(`phtssbpv-${searchId}`) ||
    getProfilerHeatmapTileSizeSelectionBySearchResultsPage(searchId)
  );
};

export const saveProfilerHeatmapTileSizeSelectionByProjectView = (size, searchId) => {
  if (typeof size !== 'string' || !searchId) {
    return;
  }

  localStorage.setItem(`phtssbpv-${searchId}`, size);
};

export const getProfilerHeatmapColorCodingSelectionByProjectView = (searchId) => {
  return (
    localStorage.getItem(`phccsbpv-${searchId}`) ||
    getProfilerHeatmapColorCodingSelectionBySearchResultsPage(searchId)
  );
};

export const saveProfilerHeatmapColorCodingSelectionByProjectView = (colorCoding, searchId) => {
  if (typeof colorCoding !== 'string' || !searchId) {
    return;
  }

  localStorage.setItem(`phccsbpv-${searchId}`, colorCoding);
};

export const hasSavedProfilerHeatmapGroupingToggleByProjectView = (searchId) => {
  const saved = localStorage.getItem(`phgtbpv-${searchId}`);

  return !!saved || hasSavedProfilerHeatmapGroupingToggleBySearchResultsPage(searchId);
};

export const getProfilerHeatmapGroupingToggleByProjectView = (searchId) => {
  const isToggled = localStorage.getItem(`phgtbpv-${searchId}`);
  if (isToggled) {
    return JSON.parse(isToggled);
  }

  return getProfilerHeatmapGroupingToggleBySearchResultsPage(searchId);
};

export const saveProfilerHeatmapGroupingToggleByProjectView = (isToggled, searchId) => {
  if (typeof isToggled !== 'boolean' || !searchId) {
    return;
  }

  localStorage.setItem(`phgtbpv-${searchId}`, JSON.stringify(isToggled));
};

/**
 * Sort profiler results by the selected sort key. Pure helper, returns a new
 * array. Shared between Projects View and Team View.
 */
export const sortProfilerResults = (results, selectedSort) => {
  if (!selectedSort || selectedSort === 'default') {
    return results;
  }
  const copy = results.slice();
  if (selectedSort === 'name-asc') {
    copy.sort((a, b) =>
      (a.name || a.companyName || '').localeCompare(b.name || b.companyName || '', 'en', {
        sensitivity: 'base'
      })
    );
    return copy;
  }
  if (selectedSort === 'name-desc') {
    copy.sort((a, b) =>
      (b.name || b.companyName || '').localeCompare(a.name || a.companyName || '', 'en', {
        sensitivity: 'base'
      })
    );
    return copy;
  }
  const [propKey, typeSort] = selectedSort.split('-');
  const sorters = {
    asc: (key) => (a, b) =>
      +(a[key] || Number.MAX_SAFE_INTEGER) - +(b[key] || Number.MAX_SAFE_INTEGER),
    desc: (key) => (a, b) =>
      +(b[key] || -Number.MAX_SAFE_INTEGER / 3) - +(a[key] || -Number.MAX_SAFE_INTEGER / 3)
  };
  if (sorters[typeSort]) {
    copy.sort(sorters[typeSort](propKey));
  }
  return copy;
};

/* --------------------------------------------------------------------------
 * TD-2200 V2.0 — Active Spotlights + column filters within a Project view.
 *
 * Mirrors the V1 SearchResults-page persistence helpers, but with two
 * differences:
 *   1. Project-specific localStorage keys (`asbpv-` / `cfbpv-`) so an
 *      analyst can override the search-page filters for a given project
 *      without writing back to the search page.
 *   2. **Fall-back to the SearchResults-page values** when the project key
 *      is empty — so a freshly opened project inherits the analyst's last
 *      filter set from the originating search (matches the existing pattern
 *      for columns / sort / view / heatmap settings).
 *
 * The Set ⇄ Array (de)serialization is shared with the V1 helpers via the
 * exported `_columnFiltersToJson` / `_columnFiltersFromJson` so the V1.10
 * ObservableSet round-trip fix automatically applies here too.
 * -------------------------------------------------------------------------- */

export const getActiveSpotlightsByProjectView = (searchId) => {
  const raw = localStorage.getItem(`asbpv-${searchId}`);
  if (raw) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
    } catch {
      // fall through to search-page inheritance
    }
  }
  // Inherit from the originating SearchResults page when the project hasn't
  // been customized yet. Returns [] if neither store has data.
  return getActiveSpotlightsBySearchResultsPage(searchId);
};

export const saveActiveSpotlightsByProjectView = (spotlightIds, searchId) => {
  if (!Array.isArray(spotlightIds) || !searchId) return;
  localStorage.setItem(`asbpv-${searchId}`, JSON.stringify(spotlightIds));
};

export const getColumnFiltersByProjectView = (searchId) => {
  const raw = localStorage.getItem(`cfbpv-${searchId}`);
  if (raw) {
    try {
      return _columnFiltersFromJson(JSON.parse(raw));
    } catch {
      // fall through to search-page inheritance
    }
  }
  // Inherit from the originating SearchResults page when the project hasn't
  // been customized yet. Returns {} if neither store has data.
  return getColumnFiltersBySearchResultsPage(searchId);
};

export const saveColumnFiltersByProjectView = (columnFilters, searchId) => {
  if (!searchId) return;
  localStorage.setItem(`cfbpv-${searchId}`, JSON.stringify(_columnFiltersToJson(columnFilters)));
};

export const PROFILER_PROJECT_VIEW_COLUMNS = {
  COMPANY_NAME: 'COMPANY_NAME',
  LOCATION: 'LOCATION',
  COMPANY_DESCRIPTION: 'COMPANY_DESCRIPTION',
  PRODUCT_PORTFOLIO_DESCRIPTION: 'PRODUCT_PORTFOLIO_DESCRIPTION',
  KEY_PRODUCTS: 'KEY_PRODUCTS',
  KEY_END_MARKETS: 'KEY_END_MARKETS',
  INDUSTRY: 'INDUSTRY',
  VALUE_CHAIN: 'VALUE_CHAIN',
  PRODUCTS: 'PRODUCTS',
  SALES: 'SALES',
  EMPLOYEES: 'EMPLOYEES',
  CAGR3Y: 'CAGR3Y',
  CAGR4Y: 'CAGR4Y',
  CAGR_PERIOD: 'CAGR_PERIOD',
  EBIT_MARGIN: 'EBIT_MARGIN',
  SITUATION: 'SITUATION',
  SHAREHOLDERS: 'SHAREHOLDERS',
  FEEDBACK: 'FEEDBACK',
  ACTIONS: 'ACTIONS'
};

export const getInitialProfilerColumnsProjectView = (
  _searchFields,
  windowWidth,
  hideEbitMargin = false
) => {
  const searchFields = _searchFields || mapFieldsToState(PROFILER_SEARCH_FIELDS);

  const isEmployeeFillled =
    !!searchFields[PROFILER_SEARCH_FIELDS.EMPLOYEES_MIN.NAME] ||
    !!searchFields[PROFILER_SEARCH_FIELDS.EMPLOYEES_MAX.NAME];
  const isEbitMarginFilled =
    !!searchFields[PROFILER_SEARCH_FIELDS.EBIT_MARGIN_MIN.NAME] ||
    !!searchFields[PROFILER_SEARCH_FIELDS.EBIT_MARGIN_MAX.NAME];
  const isCagrPeriodSelected = !!searchFields[PROFILER_SEARCH_FIELDS.CAGR_PERIOD.NAME]?.length;
  const isCagr3YearsPeriodSelected =
    searchFields[PROFILER_SEARCH_FIELDS.CAGR_PERIOD.NAME]?.[0] === 'last3years';
  const isCagr4YearsPeriodSelected =
    searchFields[PROFILER_SEARCH_FIELDS.CAGR_PERIOD.NAME]?.[0] === 'last4years';
  const isSituationSelected = !!searchFields[PROFILER_SEARCH_FIELDS.SITUATION.NAME]?.length;
  const isOwnershipSelected =
    !!searchFields[PROFILER_SEARCH_FIELDS.OWNERSHIP_STRUCTURES.NAME]?.length ||
    !!searchFields[PROFILER_SEARCH_FIELDS.LISTING_STATUS.NAME]?.length;

  const columns = [
    {
      id: PROFILER_PROJECT_VIEW_COLUMNS.COMPANY_NAME,
      disableSelect: true,
      name: 'Company name',
      minWidth: '90px',
      fragment: '1.5fr',
      selected: true
    },
    {
      id: PROFILER_PROJECT_VIEW_COLUMNS.LOCATION,
      name: 'Location',
      minWidth: '60px',
      fragment: '1fr',
      selected: true
    },
    {
      id: PROFILER_PROJECT_VIEW_COLUMNS.COMPANY_DESCRIPTION,
      name: 'Company description',
      minWidth: '120px',
      fragment: '1.5fr',
      selected: false
    },
    {
      id: PROFILER_PROJECT_VIEW_COLUMNS.PRODUCT_PORTFOLIO_DESCRIPTION,
      name: 'Portfolio description',
      minWidth: '120px',
      fragment: '1.5fr',
      selected: false
    },
    {
      id: PROFILER_PROJECT_VIEW_COLUMNS.KEY_PRODUCTS,
      name: 'Key products',
      minWidth: '100px',
      fragment: '1fr',
      selected: false
    },
    {
      id: PROFILER_PROJECT_VIEW_COLUMNS.KEY_END_MARKETS,
      name: 'Key end-markets',
      minWidth: '100px',
      fragment: '1fr',
      selected: false
    },
    {
      id: PROFILER_PROJECT_VIEW_COLUMNS.INDUSTRY,
      name: 'Industry',
      minWidth: '60px',
      fragment: '1.5fr',
      selected: true
    },
    {
      id: PROFILER_PROJECT_VIEW_COLUMNS.VALUE_CHAIN,
      name: 'Value chain',
      minWidth: '80px',
      fragment: '1.5fr',
      selected: true
    },
    {
      id: PROFILER_PROJECT_VIEW_COLUMNS.PRODUCTS,
      name: 'Products',
      minWidth: '60px',
      fragment: '1.5fr',
      selected: true
    },
    {
      id: PROFILER_PROJECT_VIEW_COLUMNS.SALES,
      name: 'Sales (€M)',
      minWidth: '90px',
      fragment: '0.5fr',
      selected: true
    },
    {
      id: PROFILER_PROJECT_VIEW_COLUMNS.EMPLOYEES,
      name: 'Employees',
      minWidth: '65px',
      fragment: '0.5fr',
      selected:
        windowWidth >= 1536
          ? true
          : isEmployeeFillled || (!isEbitMarginFilled && !isCagrPeriodSelected)
    },
    {
      id: PROFILER_PROJECT_VIEW_COLUMNS.CAGR3Y,
      name: 'CAGR 3Y (%)',
      minWidth: '75px',
      fragment: '0.5fr',
      selected:
        windowWidth >= 1536
          ? isCagr3YearsPeriodSelected
          : isCagr3YearsPeriodSelected && !isEbitMarginFilled
    },
    {
      id: PROFILER_PROJECT_VIEW_COLUMNS.CAGR4Y,
      name: 'CAGR 4Y (%)',
      minWidth: '75px',
      fragment: '0.5fr',
      selected:
        windowWidth >= 1536
          ? isCagr4YearsPeriodSelected
          : isCagr4YearsPeriodSelected && !isEbitMarginFilled
    },
    {
      id: PROFILER_PROJECT_VIEW_COLUMNS.EBIT_MARGIN,
      name: 'EBIT Margin (%)',
      minWidth: '90px',
      fragment: '0.5fr',
      selected: isEbitMarginFilled
    },
    {
      id: PROFILER_PROJECT_VIEW_COLUMNS.SITUATION,
      name: 'Situation',
      minWidth: '60px',
      fragment: '0.5fr',
      selected:
        windowWidth >= 1536 ? isSituationSelected : isSituationSelected && !isEbitMarginFilled
    },
    {
      // The saved search's basis only; the switch is on the results page.
      id: PROFILER_PROJECT_VIEW_COLUMNS.SHAREHOLDERS,
      name: 'Shareholders',
      minWidth: '160px',
      fragment: '1fr',
      selected: isOwnershipSelected
    },
    {
      id: PROFILER_PROJECT_VIEW_COLUMNS.FEEDBACK,
      name: 'Feedback results',
      // TD-2733 — 120px, down from 160px. Three feedback counters at 36px plus 6px gaps come
      // to 120px, and the heading needs 97px, so nothing is cramped. The 40px reclaimed goes
      // to the keyword columns, whose chips are what actually need the width.
      minWidth: '120px',
      fragment: '120px',
      selected: true
    },
    {
      id: PROFILER_PROJECT_VIEW_COLUMNS.ACTIONS,
      name: 'Actions',
      minWidth: '108px',
      fragment: '108px',
      minWidthResult: '108px',
      fragmentResult: '108px',
      selected: true,
      hidden: true
    }
  ];

  // TD-2499: Lite must not see the EBIT-margin column in saved project views.
  return coerceProfilerColumnsForLite(columns, hideEbitMargin);
};

/**
 * Which columns the saved-project results grid contains, for a given viewport and
 * extended-financials state.
 *
 * TD-2733 — this was written out twice, and the two copies had drifted: the column
 * header dropped five columns in the narrow extended-financials case (LOCATION,
 * INDUSTRY, VALUE_CHAIN, PRODUCTS, SITUATION) while the rows dropped only four. The
 * header therefore built one fewer grid track than the rows, and at 1201-1535px with
 * extended financials on, every column right of Situation was misaligned. The rows are
 * the correct set, so this is theirs; the header now shares it, which is what stops the
 * two from drifting again. Both must always agree — a grid template and its cells are
 * one decision, not two.
 */
export const isProfilerProjectColumnVisible = (
  column,
  { windowWidth, isProfilerExtendedFinancialsToggled }
) => {
  if (windowWidth <= 1200) {
    return column.selected;
  }

  if (windowWidth < 1536 && isProfilerExtendedFinancialsToggled) {
    return ![
      PROFILER_PROJECT_VIEW_COLUMNS.LOCATION,
      PROFILER_PROJECT_VIEW_COLUMNS.INDUSTRY,
      PROFILER_PROJECT_VIEW_COLUMNS.VALUE_CHAIN,
      PROFILER_PROJECT_VIEW_COLUMNS.PRODUCTS
    ].includes(column.id);
  }

  return column.selected;
};

/**
 * The saved-project results grid template, for the column header and for the rows.
 *
 * TD-2733 — both used to join the columns' bare `fragment` values, which are mostly `fr`
 * units. An `fr` track's automatic minimum is its content, so two grids built from the
 * same template still resolve differently when their content differs — and it does: the
 * header holds labels like "Value chain" while the cell beneath holds a short
 * value. Their tracks drifted by up to 66px, so labels sat off their columns.
 *
 * An earlier attempt pinned each `fr` track as `minmax(minWidth, fragment)` to make the two
 * grids resolve identically. That produced overlapping header labels and had to be undone:
 * `search-results-spotlights-filters.less` deliberately sets `min-width: max-content
 * !important` on every filterable header cell, so that the label and its filter chevron stay
 * on one line, and it uses `!important` precisely to beat the inline minWidth this model
 * supplies. Pinning the track below that width does not shrink the cell — it makes the cell
 * overflow into its neighbour.
 *
 * So the tracks stay content-sized, and the residual drift between the header's grid and the
 * rows' (up to ~66px, because the header's labels are longer than the values beneath them)
 * remains open. Closing it properly means deciding whether those header cells may wrap, or
 * widening the model's minimums for the filterable columns — a column-width decision rather
 * than a layout one, so it wants its own ticket and a PO look.
 *
 * What this helper still buys: one source for the *set* and order of columns, shared with the
 * row component, so the header and the rows cannot disagree on the track count again.
 */
export const buildProfilerProjectGridTemplate = (
  columns,
  { windowWidth, isProfilerExtendedFinancialsToggled, forRows = false }
) =>
  columns
    .filter((column) =>
      isProfilerProjectColumnVisible(column, { windowWidth, isProfilerExtendedFinancialsToggled })
    )
    .map((column) => {
      const isActions = column.id === PROFILER_PROJECT_VIEW_COLUMNS.ACTIONS;

      return forRows && isActions ? column.fragmentResult : column.fragment;
    })
    .join(' ');

/**
 * TD-2733 — `PROFILER_PROJECT_VIEW_VIEWS_LIST` lived here: the same three entries with
 * the same stored values as the search page's list, in a different order, with the same
 * positional `[0]/[1]/[2]` coupling TD-2720 had to unpick on search. Both surfaces now
 * read the shared definition, so the switch menu is ordered once. Re-exported from this
 * module because the ProfilerProjects and ProfilerTeam trees import their constants
 * from here.
 */
export {
  PROFILER_VIEWS,
  DEFAULT_PROFILER_VIEW,
  PROFILER_VIEWS_LIST,
  isProfilerListVisible
} from '../../utils/constants/profilerViews';
